import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

// Mobil menü yetkilendirme — Yetki Profilleri liste ekranı (standart c360 liste).
// Detay/düzenleme tam-sayfa /mobile-permissions/:id (MobilePermissionDetailPage).
const API_BASE = '/api/permission-profiles';

const ACTIVE_OPTIONS = [{ value: 'true', label: 'Aktif' }, { value: 'false', label: 'Pasif' }];

const COLUMN_FILTER_META = {
  name: { type: 'text', odataField: 'Name' },
  description: { type: 'text', odataField: 'Description' },
  isActive: { type: 'select', odataField: 'IsActive', valueFormat: 'raw', options: ACTIVE_OPTIONS },
};
const SEARCH_FIELDS = ['Name', 'Description'];

export function MobilePermissionsPage() {
  usePageMeta('Mobil Yetkilendirme', 'Temsilci grubu bazlı mobil ekran/aksiyon yetki profilleri.');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };

  // $select: görünür kolonların OData alanları (name/description/isActive tümü meta'da) + Id
  // (satır anahtarı/nav). sortField $select'e katılmaz ($orderby tam entity'ye uygulanır).
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'Name',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData,
  } = list;

  const openNew = () => navigate('/mobile-permissions/new');
  const openDetail = (row) => { if (!row) return; navigate(`/mobile-permissions/${row.id}`, { state: { row } }); };
  const openEditRow = (row) => { if (!row) return; navigate(`/mobile-permissions/${row.id}?edit=1`, { state: { row } }); };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: deleteConfirm.id }) });
      if (!res.ok) throw new Error('Kayıt silinemedi');
      showToast('success', 'Profil başarıyla silindi');
      setDeleteConfirm(null);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(null);
    } finally {
      setDeleting(false);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('name', { header: 'Profil Adı', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('description', { header: 'Açıklama', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('isActive', {
      header: 'Durum', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Aktif</StatusBadge> : <StatusBadge tone="neutral">Pasif</StatusBadge>),
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('Name', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Profil adı veya açıklama ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Mobil Yetkilendirme"
        subtitle="Temsilci grubu bazlı mobil ekran/aksiyon yetki profillerini yönetin."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Profil</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{deleteConfirm?.name}"</strong> profilini silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={{ ...s.btnDanger, opacity: deleting ? 0.6 : 1 }} onClick={handleDelete} disabled={deleting}>{deleting ? 'Siliniyor...' : 'Evet, Sil'}</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)} disabled={deleting}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default MobilePermissionsPage;
